import { useEffect, useState } from "react";
import { keepPreviousData, useQuery } from "@tanstack/react-query";

import { listChats } from "./api";
import type { ChatListResponse } from "./types";

export const SEARCH_MIN_CHARS = 3;
export const SEARCH_DEBOUNCE_MS = 250;
const SEARCH_LIMIT = 30;

function useDebouncedValue<T>(value: T, delayMs: number): T {
    const [debounced, setDebounced] = useState(value);
    useEffect(() => {
        const id = window.setTimeout(() => setDebounced(value), delayMs);
        return () => window.clearTimeout(id);
    }, [value, delayMs]);
    return debounced;
}

export const searchChatsKey = (workspaceId: string, query: string) =>
    ["v2", "chats", "search", { workspaceId, q: query }] as const;

export function useSearchChats(query: string, workspaceId: string | null | undefined) {
    const trimmed = query.trim();
    const debounced = useDebouncedValue(trimmed, SEARCH_DEBOUNCE_MS);
    const enabled = !!workspaceId && debounced.length >= SEARCH_MIN_CHARS;

    return useQuery<ChatListResponse>({
        queryKey: searchChatsKey(workspaceId ?? "", debounced),
        queryFn: () =>
            listChats({
                workspaceId: workspaceId ?? undefined,
                limit: SEARCH_LIMIT,
                search: debounced,
            }),
        enabled,
        staleTime: 10_000,
        placeholderData: keepPreviousData,
    });
}
